import { memo, useRef, useState, useCallback } from 'react'
import imageCompression from 'browser-image-compression'
import { toast } from '../services/toastService'
import { useTranslation } from 'react-i18next'
import { trackSpotModeImageAdded, trackSpotModeImageRemoved, trackSpotModeImageUploadFailed } from '../utils/analytics'

const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4 MB — hard wall; anything larger is auto-compressed

const COMPRESSION_OPTIONS = {
  maxSizeMB: 4,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
}

/**
 * Reusable image upload component with auto-compression, drag-and-drop, and mobile support.
 * Files ≤ 4 MB pass through unchanged. Files > 4 MB are compressed to ≤ 4 MB automatically.
 * @param {Array} images - Array of File objects
 * @param {Function} setImages - Function to update images state
 * @param {number} maxImages - Maximum number of images allowed (default 3)
 */
export const ImageUpload = memo(function ImageUpload({ images = [], setImages, maxImages = 3, disabled = false }) {
  const fileInputRef = useRef(null)
  const { t } = useTranslation()
  const [isDragOver, setIsDragOver] = useState(false)
  const [compressingCount, setCompressingCount] = useState(0)

  const processFiles = useCallback(async (files) => {
    if (images.length + files.length > maxImages) {
      toast.error(t('map.maxImagesExceeded', { max: maxImages }))
      files.forEach(file => trackSpotModeImageUploadFailed('max_images_exceeded', file.size, file.type))
      return
    }

    const validFiles = []
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`)
        trackSpotModeImageUploadFailed('invalid_type', file.size, file.type)
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    // Show spinner slots for each file being processed
    setCompressingCount(prev => prev + validFiles.length)

    const processedFiles = await Promise.all(
      validFiles.map(async (file) => {
        if (file.size <= MAX_FILE_SIZE) {
          setCompressingCount(prev => prev - 1)
          return file
        }
        try {
          const compressed = await imageCompression(file, COMPRESSION_OPTIONS)
          setCompressingCount(prev => prev - 1)
          return compressed
        } catch {
          setCompressingCount(prev => prev - 1)
          toast.error(`Failed to process ${file.name}`)
          trackSpotModeImageUploadFailed('compression_failed', file.size, file.type)
          return null
        }
      })
    )

    const finalFiles = processedFiles.filter(Boolean)
    if (finalFiles.length > 0) {
      setImages(prev => {
        const newImages = [...prev, ...finalFiles]
        finalFiles.forEach((file, idx) => {
          const totalCount = prev.length + idx + 1
          const isPrimary = totalCount === 1
          trackSpotModeImageAdded(totalCount, file.size, file.type, isPrimary)
        })
        return newImages
      })
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [images.length, maxImages, setImages, t])

  const handleFileSelect = (e) => {
    processFiles(Array.from(e.target.files || []))
  }

  const handleRemove = (index) => {
    const wasPrimary = index === 0
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index)
      trackSpotModeImageRemoved(newImages.length, wasPrimary)
      return newImages
    })
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    processFiles(Array.from(e.dataTransfer.files || []))
  }

  const isCompressing = compressingCount > 0

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Images <span className="text-red-500">*</span>
        <span className="text-xs text-gray-500 ml-2">(1-{maxImages} required)</span>
      </label>

      {/* Thumbnail grid — shows existing images + in-progress compression spinners */}
      {(images.length > 0 || isCompressing) && (
        <div className="mb-3 grid grid-cols-3 gap-2">
          {images.map((file, index) => (
            <div key={index} className="relative group">
              <img
                src={URL.createObjectURL(file)}
                alt={`Upload ${index + 1}`}
                className="w-full h-32 object-cover rounded-md border border-gray-200"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                onTouchEnd={(e) => { e.preventDefault(); handleRemove(index) }}
                disabled={disabled}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 can-hover:opacity-0 can-hover:group-hover:opacity-100 transition-opacity disabled:opacity-50"
                title="Remove image"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {index === 0 && (
                <div className="absolute bottom-1 left-1 bg-primary text-white text-xs px-2 py-0.5 rounded-md">
                  Primary
                </div>
              )}
            </div>
          ))}
          {isCompressing && Array.from({ length: compressingCount }).map((_, i) => (
            <div
              key={`compressing-${i}`}
              className="flex items-center justify-center w-full h-32 bg-gray-100 rounded-md border border-gray-200"
            >
              <div className="flex flex-col items-center gap-1 text-gray-500">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-xs">{t('map.optimizingImage')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drag-and-drop / browse zone */}
      {images.length < maxImages && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleBrowseClick}
            onTouchEnd={(e) => { e.preventDefault(); handleBrowseClick() }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={disabled || isCompressing}
            className={`w-full px-4 py-5 border-2 border-dashed rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2 ${
              isDragOver
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-300 hover:border-primary hover:bg-gray-50 text-gray-600'
            }`}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm font-medium">{t('map.dragPhotosHere')}</span>
          </button>
          <p className="text-xs text-gray-500 mt-1">
            {t('map.maxFileSizeMessage')}
          </p>
        </div>
      )}
    </div>
  )
})
